import React, { useState } from 'react';
import { Box, Typography, Button, Modal, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import apiClient from '../../../../api/apiClient';
import extractApiError from '../../../../api/extractApiError';
import { ACTION_BLUE } from '../../../../theme/theme';

interface UnpublishAppModalProps {
  open: boolean;
  onClose: () => void;
  publisherId: string;
  appId: string;
  onConfirm: () => void;
}

/**
 * Withdraws the application from the store.
 *
 * Deliberately separate from DeactivateAppModal: deactivating only unlists
 * the application and a deeplink install still works, while this stops it
 * being served at all and needs sibche to publish it again.
 */
const UnpublishAppModal: React.FC<UnpublishAppModalProps> = ({
  open,
  onClose,
  publisherId,
  appId,
  onConfirm,
}) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);

    try {
      await apiClient.post('/v2/publisher/applications/unpublish', null, {
        params: {
          publisher_id: publisherId,
          application_id: appId,
        },
      });

      queryClient.invalidateQueries({
        queryKey: ['applicationDetails', publisherId, appId],
      });

      toast.success('اپلیکیشن از فروشگاه برداشته شد');
      onConfirm();
    } catch (error) {
      toast.error(extractApiError(error, 'خطا در برداشتن اپلیکیشن از فروشگاه'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 24,
          p: 16,
          m: 8,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            textAlign: 'right',
            color: '#25282E',
            fontSize: 16,
            fontFamily: 'IRANYekanXFaNum',
            fontWeight: 700,
            lineHeight: '20px',
          }}
        >
          برداشتن اپلیکیشن از فروشگاه
        </Typography>

        <Typography
          sx={{
            mt: 2,
            textAlign: 'right',
            color: '#60646E',
            fontSize: 14,
            fontFamily: 'IRANYekanXFaNum',
            fontWeight: 600,
          }}
        >
          با این کار اپلیکیشن به‌طور کامل از سیبچه برداشته می‌شود و دیگر قابل
          مشاهده و دانلود نخواهد بود. برای بازگشت به فروشگاه، باید دوباره توسط
          سیبچه بررسی و منتشر شود.
        </Typography>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 16 }}
        >
          <Button
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: '#FFD6DB',
              color: '#EF4056',
              fontSize: 14,
              fontFamily: 'IRANYekanXFaNum',
              fontWeight: 700,
              '&:hover': { backgroundColor: '#FFD6DB' },
            }}
            onClick={handleConfirm}
          >
            {loading ? 'در حال انجام...' : 'تایید و برداشتن'}
          </Button>

          <Button
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: ACTION_BLUE,
              color: 'white',
              fontSize: 12,
              fontFamily: 'IRANYekanXFaNum',
              fontWeight: 500,
              '&:hover': { backgroundColor: ACTION_BLUE },
            }}
            onClick={onClose}
          >
            انصراف
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default UnpublishAppModal;
